import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";

import HisLogo from "../../../ui library/components/svgs/standard/library/his-logo";
import Svg, { SvgEnum } from "../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../ui library/HNSColors";

const TITLE_ICON_NAMES = {
  fee: SvgEnum.HisCalculator,
  medical: SvgEnum.HisClipboardList,
  reduction: SvgEnum.HisTrendingDown,
  settlement: SvgEnum.HisClipboardCheck,
};

function createCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load generated export image"));
    image.src = src;
  });
}

function extractSvgMarkup(markup) {
  const start = markup.indexOf("<svg");
  const end = markup.lastIndexOf("</svg>");

  if (start === -1 || end === -1) {
    throw new Error("Rendered export icon did not produce valid SVG markup");
  }

  return markup.slice(start, end + 6);
}

function addSvgNamespace(svgMarkup) {
  if (svgMarkup.includes("xmlns=")) {
    return svgMarkup;
  }

  return svgMarkup.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
}

function reactSvgToDataUrl(svgElement) {
  const markup = renderToStaticMarkup(svgElement);
  const svgMarkup = addSvgNamespace(extractSvgMarkup(markup));

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
}

async function drawReactSvg(ctx, svgElement, x, y, width, height) {
  const image = await loadImage(reactSvgToDataUrl(svgElement));
  ctx.drawImage(image, x, y, width, height);
}

async function createBannerImageDataUrl() {
  const canvas = createCanvas(1600, 200);
  const ctx = canvas.getContext("2d");
  const gradient = ctx.createLinearGradient(0, 0, 1600, 0);

  gradient.addColorStop(0, HNSColors.secondary[300]);
  gradient.addColorStop(0.8221, HNSColors.secondary[450]);
  gradient.addColorStop(1, HNSColors.tertiary[500]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  await drawReactSvg(ctx, createElement(HisLogo, { sx: { width: "145px" } }), 72, 44, 310, 106);
  ctx.fillStyle = HNSColors.neutrals[900];
  ctx.font = "500 22px Arial";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  ctx.fillText("Wellness, Simplified.", 1290, 100);

  return canvas.toDataURL("image/png");
}

async function createSectionTitleImageDataUrl(title, iconName) {
  const canvas = createCanvas(1100, 96);
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = HNSColors.neutrals[900];
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await drawReactSvg(
    ctx,
    createElement(Svg, { name: iconName, color: HNSColors.primary[500], sx: { width: "32px", height: "32px" } }),
    0,
    27,
    32,
    32
  );
  ctx.fillStyle = HNSColors.primary[500];
  ctx.font = "600 26px Arial";
  ctx.textBaseline = "middle";
  ctx.fillText(title, 52, 43);

  return canvas.toDataURL("image/png");
}

export function formatPdfCurrency(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

export async function buildSettlementPdfAssets(feeRows) {
  const [bannerUrl, settlement, medical, reduction, fee, pieChartUrl] = await Promise.all([
    createBannerImageDataUrl(),
    createSectionTitleImageDataUrl("Client Details", TITLE_ICON_NAMES.settlement),
    createSectionTitleImageDataUrl("Medical Breakdown", TITLE_ICON_NAMES.medical),
    createSectionTitleImageDataUrl("Total Medical Reduction", TITLE_ICON_NAMES.reduction),
    createSectionTitleImageDataUrl("Fee Schedule Calculator", TITLE_ICON_NAMES.fee),
    createFeeSchedulePieChartDataUrl(feeRows),
  ]);

  return { bannerUrl, pieChartUrl, sectionTitleUrls: { fee, medical, reduction, settlement } };
}

async function createFeeSchedulePieChartDataUrl(rows) {
  const canvas = createCanvas(700, 420);
  const ctx = canvas.getContext("2d");
  const chartRows = rows.filter((row) => Number(row.percent || 0) > 0);
  const total = chartRows.reduce((sum, row) => sum + Number(row.percent || 0), 0);
  const centerX = 210;
  const centerY = 200;
  const radius = 120;

  ctx.fillStyle = HNSColors.neutrals[900];
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  let currentAngle = -Math.PI / 2;

  (total ? chartRows : [{ color: HNSColors.tertiary[900], percent: 100 }]).forEach((row) => {
    const angle = (Number(row.percent || 0) / (total || 100)) * Math.PI * 2;

    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + angle);
    ctx.closePath();
    ctx.fillStyle = row.color;
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = HNSColors.neutrals[900];
    ctx.stroke();
    currentAngle += angle;
  });

  ctx.font = "600 19px Arial";
  chartRows.forEach((row, index) => {
    const y = 96 + index * 58;
    ctx.fillStyle = row.color;
    ctx.fillRect(400, y - 14, 18, 18);
    ctx.fillStyle = HNSColors.neutrals[300];
    ctx.fillText(`${row.label} (${Number(row.percent || 0).toFixed(2)}%)`, 432, y);
  });

  return canvas.toDataURL("image/png");
}
